import api from './api';
import { summarizeReolinkReceive } from '../modules/sensor-data/utils/reolinkVideoReceive';

/**
 * Alerts video — Reolink bridge APIs only.
 *
 * GET /api/bridges/{bridgeId}/reolink-videos?fileType=video
 * GET /api/bridges/{bridgeId}/reolink-videos/{id}/url
 *
 * Do NOT use /api/cameras. Do NOT open s3Key. Do NOT invent filenames.
 * Auth: Bearer JWT via shared `api` client (base URL already includes /api).
 */

const extractVideos = (res) => {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res?.videos)) return res.videos;
  if (Array.isArray(res?.data?.data)) return res.data.data;
  if (Array.isArray(res?.data?.videos)) return res.data.videos;
  return [];
};

const fileNameFromKey = (value) => {
  const text = String(value || '');
  if (!text) return '';
  const parts = text.split('/');
  return parts[parts.length - 1] || text;
};

const hydrateReolinkVideo = (video) => {
  if (!video || typeof video !== 'object') return video;
  const s3Key = video.s3Key || video.key || '';
  const fileName = video.fileName || video.name || fileNameFromKey(s3Key);
  return {
    ...video,
    fileName,
    recordedAt: video.recordedAt || video.lastModified,
    lastModified: video.lastModified || video.recordedAt,
  };
};

const isReolinkVideoFile = (video) => {
  const type = `${video?.fileType || ''} ${video?.mimeType || ''}`.toLowerCase();
  const name = `${video?.fileName || ''} ${video?.s3Key || ''}`.toLowerCase();
  if (type.includes('image') || /\.(jpe?g|png|gif|webp)$/i.test(name)) return false;
  if (type.includes('video') || /\.(mp4|mov|m4v|avi)$/i.test(name)) return true;
  return !type.includes('image');
};

/** Prefer list item `id` (base64 key); fall back to `_id`. */
export const resolveReolinkVideoId = (video) => {
  if (!video) return null;
  if (video.id != null && video.id !== '') return String(video.id);
  if (video._id != null && video._id !== '') return String(video._id);
  return null;
};

const extractUrlPayload = (res) => {
  if (res?.data && typeof res.data === 'object' && !Array.isArray(res.data)) {
    return res.data;
  }
  if (res && typeof res === 'object' && res.url) return res;
  return res?.data || res || {};
};

/**
 * No list cap. AlertsVideoPanel filters by data logger after this returns.
 * The bridge list walks S3 oldest-first and can stop before a newer clip.
 * A second call for the last 31 day folders is merged in so that clip is not dropped.
 */
const FIELD_HIGHLIGHT_LIST_TIMEOUT_MS = 120000;
const FIELD_HIGHLIGHT_MAX_PAGES = 40;
const FIELD_HIGHLIGHT_RECENT_DAY_SPAN = 30;
const fieldHighlightListCache = new Map();

const fieldHighlightListCacheKey = (bridgeId) => String(bridgeId);

const utcDateParam = (dayOffset) => {
  const now = new Date();
  const day = new Date(Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate() + dayOffset,
  ));
  const month = String(day.getUTCMonth() + 1).padStart(2, '0');
  const date = String(day.getUTCDate()).padStart(2, '0');
  return `${day.getUTCFullYear()}-${month}-${date}`;
};

const pushReolinkVideos = (res, videos, seen) => {
  let added = 0;
  extractVideos(res).map(hydrateReolinkVideo).filter(isReolinkVideoFile).forEach((video) => {
    const id = resolveReolinkVideoId(video) || video.s3Key || video.key || video.fileName;
    if (!id || seen.has(id)) return;
    seen.add(id);
    videos.push(video);
    added += 1;
  });
  return added;
};

const buildFieldHighlightListResult = (res, bridgeId, videosOverride) => {
  const videos = videosOverride
    || extractVideos(res).map(hydrateReolinkVideo).filter(isReolinkVideoFile);
  return {
    videos,
    count: videosOverride ? videos.length : (Number(res?.count) || videos.length),
    bridgeId: res?.bridgeId || bridgeId,
    bridgeName: res?.bridgeName || null,
    reolinkBattery: res?.reolinkBattery || res?.data?.reolinkBattery || null,
    reolinkBatteries: Array.isArray(res?.reolinkBatteries)
      ? res.reolinkBatteries
      : (Array.isArray(res?.data?.reolinkBatteries) ? res.data.reolinkBatteries : []),
    receive: summarizeReolinkReceive(videos, res),
    raw: res,
  };
};

const reolinkVideoApi = {
  /** GET /api/bridges/{bridgeId}/reolink-videos?fileType=video */
  listByBridge: async (bridgeId, options = {}) => {
    if (!bridgeId) throw new Error('Bridge ID is required');

    const cacheKey = fieldHighlightListCacheKey(bridgeId);
    if (!options.force && fieldHighlightListCache.has(cacheKey)) {
      return fieldHighlightListCache.get(cacheKey);
    }

    const videos = [];
    const seen = new Set();
    let nextToken = '';
    let pages = 0;
    let lastRes = null;
    let listError = null;
    const timeout = options.timeout ?? FIELD_HIGHLIGHT_LIST_TIMEOUT_MS;
    const listUrl = `/bridges/${encodeURIComponent(String(bridgeId))}/reolink-videos`;

    try {
      do {
        const params = {
          fileType: 'video',
          sort: 'desc',
          order: 'desc',
        };
        if (nextToken) params.nextToken = nextToken;

        const res = await api.get(listUrl, params, { timeout });
        lastRes = res;

        const added = pushReolinkVideos(res, videos, seen);
        nextToken = res?.isTruncated && res?.nextToken ? String(res.nextToken) : '';
        pages += 1;
        if (!added) break;
      } while (nextToken && pages < FIELD_HIGHLIGHT_MAX_PAGES);
    } catch (err) {
      listError = err;
    }

    try {
      const recentRes = await api.get(
        listUrl,
        {
          fileType: 'video',
          sort: 'desc',
          order: 'desc',
          startDate: utcDateParam(-FIELD_HIGHLIGHT_RECENT_DAY_SPAN),
          endDate: utcDateParam(0),
        },
        { timeout },
      );
      pushReolinkVideos(recentRes, videos, seen);
      if (!lastRes) lastRes = recentRes;
    } catch {
      // Day-folder listing is additive. The bridge list above still stands.
    }

    if (!videos.length && listError) throw listError;

    const result = buildFieldHighlightListResult(lastRes, bridgeId, videos);
    fieldHighlightListCache.set(cacheKey, result);
    return result;
  },

  peekListByBridge(bridgeId) {
    if (!bridgeId) return null;
    return fieldHighlightListCache.get(fieldHighlightListCacheKey(bridgeId)) || null;
  },

  /**
   * GET /api/bridges/{bridgeId}/reolink-videos/{id}/url
   * `{id}` must be the list item `id` field.
   */
  getPlayUrl: async (bridgeId, videoId) => {
    if (!bridgeId || !videoId) {
      throw new Error('Bridge ID and video id are required');
    }

    const res = await api.get(
      `/bridges/${encodeURIComponent(String(bridgeId))}/reolink-videos/${encodeURIComponent(String(videoId))}/url`
    );

    const data = extractUrlPayload(res);
    const url = data?.url || null;
    if (!url) {
      throw new Error(res?.message || 'Could not get video URL');
    }

    const expiresIn = Number(data?.expiresIn);
    return {
      url,
      expiresIn: Number.isFinite(expiresIn) && expiresIn > 0 ? expiresIn : 900,
      fileName: data?.fileName || null,
      mimeType: data?.mimeType || null,
      fileType: data?.fileType || null,
      raw: res,
    };
  },

  /**
   * GET /api/bridges/{bridgeId}/reolink-battery
   * Camera battery % — not sensor batteryVoltage. Never required for video play.
   */
  getBatteryByBridge: async (bridgeId) => {
    if (!bridgeId) throw new Error('Bridge ID is required');
    const res = await api.get(
      `/bridges/${encodeURIComponent(String(bridgeId))}/reolink-battery`
    );
    return res;
  },
};

export default reolinkVideoApi;
